import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { getImpersonatedWorkspaceId } from "@/components/layout/WorkspaceSelector";

export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled" | "pending_verification";
export type SubscriptionPlan = "basic" | "pro" | "enterprise" | "superadmin";

interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  max_users: number;
  trial_ends_at: string | null;
  current_period_end: string | null;
}

interface Workspace {
  id: string;
  name: string;
  owner_id: string;
  subscription: Subscription | null;
}

interface UseWorkspaceReturn {
  workspace: Workspace | null;
  loading: boolean;
  error: string | null;
  isOwner: boolean;
  hasActiveSubscription: boolean;
  currentPlan: SubscriptionPlan | null;
  planModules: string[];
  canAccessModule: (module: string) => boolean;
  refreshWorkspace: () => Promise<void>;
}

export function useWorkspace(): UseWorkspaceReturn {
  const { user } = useAuth();
  const { isSuperAdmin } = useSuperAdmin();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planModules, setPlanModules] = useState<string[]>([]);

  const userId = user?.id;
  const impersonatedId = isSuperAdmin ? getImpersonatedWorkspaceId() : null;

  const fetchPlanModules = async (plan: SubscriptionPlan) => {
    try {
      const { data: settings, error } = await supabase
        .from("saas_payment_settings_public")
        .select("basic_modules, pro_modules, enterprise_modules")
        .limit(1)
        .maybeSingle();

      if (error) {
        logger.error("Error fetching plan modules:", error);
        return;
      }

      if (settings) {
        let modules: string[] = [];
        if (plan === "basic") {
          modules = settings.basic_modules || [];
        } else if (plan === "pro") {
          modules = settings.pro_modules || [];
        } else if (plan === "enterprise") {
          modules = settings.enterprise_modules || [];
        }
        setPlanModules(modules);
      }
    } catch (err) {
      console.error("Error fetching plan modules:", err);
    }
  };

  const fetchWorkspace = useCallback(async () => {
    if (!userId) {
      setWorkspace(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. If superadmin has selected a workspace to impersonate
      if (isSuperAdmin && impersonatedId) {
        const { data: impersonatedWs, error: impError } = await supabase
          .from("workspaces")
          .select(`
            id,
            name,
            owner_id,
            subscriptions (
              id,
              plan,
              status,
              max_users,
              trial_ends_at,
              current_period_end
            )
          `)
          .eq("id", impersonatedId)
          .maybeSingle();

        if (impError) throw impError;

        if (impersonatedWs) {
          const subscription = Array.isArray(impersonatedWs.subscriptions) ? impersonatedWs.subscriptions?.[0] : impersonatedWs.subscriptions || null;
          setWorkspace({
            id: impersonatedWs.id,
            name: impersonatedWs.name,
            owner_id: impersonatedWs.owner_id,
            subscription: subscription ? {
              id: subscription.id,
              plan: subscription.plan as SubscriptionPlan,
              status: subscription.status as SubscriptionStatus,
              max_users: subscription.max_users,
              trial_ends_at: subscription.trial_ends_at,
              current_period_end: subscription.current_period_end,
            } : null,
          });
          if (subscription?.plan) {
            await fetchPlanModules(subscription.plan as SubscriptionPlan);
          }
          setLoading(false);
          return;
        }
      }

      // 2. Check if user owns a workspace
      const { data: ownedWorkspaces, error: ownedError } = await supabase
        .from("workspaces")
        .select(`
          id,
          name,
          owner_id,
          subscriptions (
            id,
            plan,
            status,
            max_users,
            trial_ends_at,
            current_period_end
          )
        `)
        .eq("owner_id", userId);

      if (ownedError) throw ownedError;

      if (ownedWorkspaces && ownedWorkspaces.length > 0) {
        const ownedWorkspace = ownedWorkspaces[0];
        const subscription = Array.isArray(ownedWorkspace.subscriptions) ? ownedWorkspace.subscriptions?.[0] : ownedWorkspace.subscriptions || null;
        
        setWorkspace({
          id: ownedWorkspace.id,
          name: ownedWorkspace.name,
          owner_id: ownedWorkspace.owner_id,
          subscription: subscription ? {
            id: subscription.id,
            plan: subscription.plan as SubscriptionPlan,
            status: subscription.status as SubscriptionStatus,
            max_users: subscription.max_users,
            trial_ends_at: subscription.trial_ends_at,
            current_period_end: subscription.current_period_end,
          } : null,
        });

        if (subscription?.plan) {
          await fetchPlanModules(subscription.plan as SubscriptionPlan);
        }
        setLoading(false);
        return;
      }

      // 3. Check if user is a member
      const { data: membership, error: memberError } = await supabase
        .from("workspace_members")
        .select(`
          workspace_id,
          workspaces (
            id,
            name,
            owner_id,
            subscriptions (
              id,
              plan,
              status,
              max_users,
              trial_ends_at,
              current_period_end
            )
          )
        `)
        .eq("user_id", userId)
        .maybeSingle();

      if (memberError && memberError.code !== "PGRST116") throw memberError;

      if (membership?.workspaces) {
        const ws = membership.workspaces as any;
        const subscription = Array.isArray(ws.subscriptions) ? ws.subscriptions?.[0] : ws.subscriptions || null;
        setWorkspace({
          id: ws.id,
          name: ws.name,
          owner_id: ws.owner_id,
          subscription: subscription ? {
            id: subscription.id,
            plan: subscription.plan as SubscriptionPlan,
            status: subscription.status as SubscriptionStatus,
            max_users: subscription.max_users,
            trial_ends_at: subscription.trial_ends_at,
            current_period_end: subscription.current_period_end,
          } : null,
        });

        if (subscription?.plan) {
          await fetchPlanModules(subscription.plan as SubscriptionPlan);
        }
        setLoading(false);
        return;
      }

      setWorkspace(null);
    } catch (err) {
      logger.error("Error fetching workspace:", err);
      setError(err instanceof Error ? err.message : "Error fetching workspace");
    } finally {
      setLoading(false);
    }
  }, [userId, isSuperAdmin, impersonatedId]);

  useEffect(() => {
    fetchWorkspace();

    const handleWorkspaceChanged = () => {
      fetchWorkspace();
    };

    window.addEventListener("workspace-changed", handleWorkspaceChanged);
    return () => {
      window.removeEventListener("workspace-changed", handleWorkspaceChanged);
    };
  }, [fetchWorkspace]);


  const isOwner = workspace?.owner_id === userId;
  const hasActiveSubscription = 
    isSuperAdmin ||
    workspace?.subscription?.status === "active" || 
    workspace?.subscription?.status === "trialing";

  const currentPlan = isSuperAdmin ? "enterprise" : (workspace?.subscription?.plan || null);

  const canAccessModule = useCallback((module: string): boolean => {
    if (isSuperAdmin) return true;
    if (!workspace || !hasActiveSubscription) return false;
    if (planModules.length === 0) return true;
    return planModules.includes(module);
  }, [workspace, hasActiveSubscription, planModules, isSuperAdmin]);

  return {
    workspace,
    loading,
    error,
    isOwner,
    hasActiveSubscription,
    currentPlan,
    planModules,
    canAccessModule,
    refreshWorkspace: fetchWorkspace,
  };
}